import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { backend } from '@/api/backend';
import { ApiError, newIdempotencyKey } from '@/api/http';
import { Button, Panel, PanelHeader } from '@/components/ui';
import { Facts } from '@/components/Facts';
import { GateChecks, readGate, POPULATION_OPEN_STATES } from '@/components/GateChecks';
import { namingAllowed, populationProgress, shippingState } from '@/pages/shipping';
import { populationVerdict, stalledSentence } from '@/pages/populationWatch';
import { deliveryFailure } from '@/pages/deliveryFailure';
import { DeliveredFiles } from '@/components/DeliveredFiles';
import { explainLine } from '@/api/explain';
import { say } from '@/lib/vocabulary';
import { cn } from '@/lib/cn';
import { useAuth } from '@/auth/auth';
import { can, cannotBecause } from '@/auth/can';
import { Confirm } from '@/components/Confirm';
import { CurationFlags, readFlags } from '@/components/CurationFlags';
import { Details, StepCard } from '@/components/StepCard';
import type { components } from '@/api/generated/openapi';
import { Working } from '@/components/Working';
import { FolderLayout, readLayouts } from '@/components/FolderLayout';
import { PackageTree, readTree } from '@/components/PackageTree';
import { PlacementRules, readRules } from '@/components/PlacementRules';
import { PACKAGING_EVENTS, packagingStreamUrl, useLiveStream } from '@/api/stream';
import { formatBytes } from '@/lib/format';
import { liveStep, stepState as cardState, type WizardStep } from '@/pages/wizard';
import { attemptKey, hasFailed, shouldRetry, MAX_ATTEMPTS } from '@/pages/deliveryRetry';

/**
 * The packaging run, driven to the end.
 *
 * The backend proved this whole path on live data - discovery, curation,
 * naming, documents, exports with verified digests, gate - so the product
 * has to be able to walk it without an operator reaching for curl. Each
 * state shows the data that state produced and the single action that
 * advances it. The run's own `state` decides which one is current; nothing
 * here infers a step from the absence of another.
 *
 * The workflow reaches a handover end to end now. The gate seals its own
 * evaluation, so GATE_PASSED is reachable for the first time in the system's
 * history, and the delivery edges beyond it with it. What stops a run today is
 * a package with nothing placed in it, which is curation rather than a closed
 * end.
 */

type PackagingDelivery = components['schemas']['PackagingDelivery'];

const retry = (n: number, e: unknown) => (e instanceof ApiError ? e.retryable && n < 2 : n < 2);

const AXES = ['PRODUCT', 'FEATURE', 'PERSONA'] as const;

/**
 * One step's read. `poll` is how the page learns a background job finished.
 *
 * Every step here was fetched once and never again, which is why pressing a
 * control that dispatches work looked like it did nothing: the POST returned,
 * the button re-enabled, and the page went on showing the state from before
 * the press for as long as it stayed open. The work was running the whole
 * time and nothing on screen could say so, because nothing asked.
 */
function useStep(
  runId: string,
  key: string,
  fn: (signal?: AbortSignal) => Promise<unknown>,
  on: boolean,
  poll: number | false | ((q: { state: { data?: unknown } }) => number | false) = false,
) {
  return useQuery({
    queryKey: ['packaging', key, runId],
    queryFn: ({ signal }) => fn(signal),
    enabled: on,
    retry,
    refetchInterval: poll,
  });
}

const unwrap = (v: unknown) => (v as { data?: unknown } | null)?.data ?? v;

/** A delivery that has ended: nothing left to watch. */
const DELIVERY_TERMINAL = new Set(['DELIVERED', 'FAILED', 'REFUSED', 'CANCELLED']);

/**
 * Poll a delivery while it is still moving, and stop when it is not.
 *
 * Written as a predicate over the query's own data rather than the run's
 * state, because 0078 lets a delivery finish while its run stays at
 * GATE_PASSED - so the run is not a reliable signal that the copy is over.
 */
const DELIVERY_POLL_MS = (q: { state: { data?: unknown } }): number | false => {
  const st = (unwrap(q.state.data) as { state?: string } | undefined)?.state;
  if (!st) return 5000;
  return DELIVERY_TERMINAL.has(st) ? false : 5000;
};

/**
 * The run's gap register, when discovery has recorded one.
 *
 * A field the register does not carry is unknown. It used to become zero,
 * which meant an incomplete payload rendered a reassuring "0 blocking" under
 * a heading about problems found.
 */
function gapSummary(
  value: unknown,
): { open: number | null; blockingOpen: number | null; acknowledged: number | null; resolved: number | null } | null {
  const g = (value as { gapSummary?: Record<string, unknown> } | null)?.gapSummary;
  if (!g || typeof g !== 'object') return null;
  const n = (k: string) => (typeof g[k] === 'number' ? (g[k] as number) : null);
  return { open: n('open'), blockingOpen: n('blockingOpen'), acknowledged: n('acknowledged'), resolved: n('resolved') };
}

/**
 * The decisions this wizard asks for, in the order it asks for them.
 *
 * The numbers used to be written by hand on each card and had drifted into
 * 1, 2, 3, 4, 7, 6 with no step 5, two different totals, and step 7 rendered
 * two hundred lines above step 6. A wizard owes its user a coherent count
 * before it owes them anything else, so the count is derived here and the
 * cards read from it.
 */
const STEPS = ['include', 'layout', 'stock', 'name', 'documents', 'files', 'gate', 'deliver'] as const;
type StepId = (typeof STEPS)[number];
const stepNo = (id: StepId) => STEPS.indexOf(id) + 1;
const STEP_COUNT = STEPS.length;

/**
 * What happened when this control was last pressed, rendered at the control.
 *
 * Every outcome used to funnel into one orange paragraph at the top of a
 * nine-hundred-line page. Press Download and the confirmation appeared five
 * hundred lines above, in the colour used for warnings, usually off-screen.
 * Success read as failure and failure read as nothing at all, so the button
 * got pressed again, which for an export permanently adds a second copy.
 */
function ActionNote({
  when,
  noteFor,
  note,
  ok,
}: {
  when: string;
  noteFor: string | null;
  note: string | null;
  ok: boolean;
}) {
  if (!note || noteFor !== when) return null;
  return (
    <span
      role="status"
      className={cn(
        'mt-1.5 block w-full rounded border px-2.5 py-1.5 text-caption',
        ok ? 'border-ok/40 bg-ok-soft text-ink-2' : 'border-warn/40 bg-warn-soft text-ink-2',
      )}
    >
      {note}
    </span>
  );
}

/** What each running action is, for the strip that explains the lock. */
const WORKING: Record<string, string> = {
  toolset: 'Recording which tools ship',
  population: 'Taking stock of every file',
  'keep-all': 'Keeping every flagged file',
  'drop-many': 'Leaving files out',
  name: 'Naming the package',
  rule: 'Saving a placement rule',
  gate: 'Running the release checks',
  delivery: 'Requesting the handover',
};

/** The layout choices, said as folders rather than as the schema's axis. */
/** The grouping the instruction calls the usual one, marked on the control. */
const RECOMMENDED_AXIS = 'PRODUCT';

const AXIS_LABEL: Record<string, string> = {
  PRODUCT: 'one folder per product',
  FEATURE: 'one folder per feature',
  PERSONA: 'one folder per team',
};

/** A count the API reported, or a mark saying it did not report one. */
const count = (n: number | undefined) => (typeof n === 'number' ? n.toLocaleString() : '—');

/**
 * Labels that render their own outcome next to their control.
 *
 * Anything act() is called with that is not in here falls back to the page
 * note, so no write can complete or fail in silence. Adding an ActionNote for
 * a label means adding it here too, and forgetting to is now visible rather
 * than silent: the outcome simply appears at the top instead.
 */
const INLINE_NOTE = new Set(['toolset', 'population', 'name', 'gate', 'delivery']);

export function PackagingSteps({
  runId,
  bucketId,
  state,
  stateChangedAt,
  discovery,
  exportIds,
  onExportCreated,
}: {
  runId: string;
  /** The bucket this run packages, so a blocked card can offer the way back. */
  bucketId: string;
  state: string;
  /** When the run last moved, so a held run's clock measures the real wait. */
  stateChangedAt?: string;
  discovery?: { data?: unknown };
  /** Export ids this run has built. No route lists them, so they are held by
   *  the caller and survive a reload in the URL. */
  exportIds: string[];
  onExportCreated: (id: string) => void;
}) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState<string | null>(null);
  /**
   * A dispatched background job, held until the server says it landed.
   *
   * `busy` clears when the POST resolves, and for a job that runs in the
   * background that is the moment the work STARTS. So the one control that
   * kicks off the longest step in the product went back to looking untouched
   * a few hundred milliseconds after it was pressed, with a green "Started"
   * beside it and nothing else ever changing.
   */
  const [watching, setWatching] = useState<string | null>(null);
  /**
   * When the population was dispatched from this page, so a job that produces
   * nothing can be told from one that is merely slow. The watch previously had
   * no exit but success, and a refused population left it spinning for good.
   */
  const [watchStartedAt, setWatchStartedAt] = useState<number | null>(null);
  const [tick, setTick] = useState(() => Date.now());
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  // Mirrored in a ref so the catch can read it after `finally` has cleared it.
  const progressRef = useRef<{ done: number; total: number } | null>(null);
  progressRef.current = progress;
  const [note, setNote] = useState<string | null>(null);
  // Which action the note belongs to, so a refusal appears where it was caused.
  const [noteFor, setNoteFor] = useState<string | null>(null);
  /** Whether the last outcome was a success. Every note used to render in
   *  warning orange, so a completed download read as a failure. */
  const [noteOk, setNoteOk] = useState(true);
  const [bucketName, setBucketName] = useState('');
  /**
   * Whether the operator has typed a name of their own.
   *
   * The field started empty with the proposals folded into a select nobody
   * opens, so the default the backend computes - the source bucket's own name
   * with a `-production` suffix, which is the whole point of naming a delivery
   * after what it came from - was one click away from being invisible. The
   * first candidate is offered as the value; anything typed wins and is never
   * overwritten by a later read.
   */
  const [nameTouched, setNameTouched] = useState(false);
  const [title, setTitle] = useState('');

  // Backend authorization is two-sided; this is the global half, and when it
  // says no the write is certain to be refused. Offering it anyway is how a
  // viewer learns their permissions by pressing something irreversible.
  const { user } = useAuth();
  const mayOperate = can(user?.role, 'operate');
  const mayReview = can(user?.role, 'review');
  const mayDeliver = can(user?.role, 'deliver');

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['packaging-run', runId] });
    void qc.invalidateQueries({ queryKey: ['packaging'] });
  };

  // Packaging publishes its own events. Population registration in particular
  // runs on an executor for minutes on a large bucket, and the page had no way
  // to know it had finished except the operator reloading.
  // Gated like the other two surfaces. A run that has reached a terminal
  // state has nothing further to publish, and without this the page
  // reconnected to a closed stream every few seconds for as long as it
  // stayed open.
  const packagingLive = !['DELIVERED', 'GATE_FAILED', 'DELIVERY_FAILED'].includes(state);
  useLiveStream(packagingLive ? packagingStreamUrl(runId) : null, PACKAGING_EVENTS, () => refresh());

  /**
   * Decides every outstanding flag KEEP.
   *
   * Hoisted so the gate criterion can run exactly this, rather than a second
   * copy that could drift from it. Strictly sequential with a retry, because
   * packaging persistence answers DEPENDENCY_UNAVAILABLE under concurrent
   * writes and abandoning halfway leaves the run undecidable.
   */
  /**
   * Decide a set of flags one at a time.
   *
   * Packaging persistence answers DEPENDENCY_UNAVAILABLE under concurrent
   * writes, and abandoning halfway leaves the run undecidable, so each write
   * waits for the last and retries a retryable failure. The per-kind bulk
   * buttons used to fire the whole set at once through a synchronous forEach
   * over an async handler, which is the failure this loop exists to avoid.
   */
  const decideMany = (ids: string[], decision: 'KEEP' | 'DROP', said: string) =>
    act(
      decision === 'KEEP' ? 'keep-all' : 'drop-many',
      async () => {
        setProgress({ done: 0, total: ids.length });
        for (const [i, id] of ids.entries()) {
          for (let attempt = 0; ; attempt++) {
            try {
              await backend.decidePackagingCuration(runId, id, decision, newIdempotencyKey());
              break;
            } catch (e) {
              const retryable = e instanceof ApiError && e.retryable && attempt < 4;
              if (!retryable) throw e;
              await new Promise((r) => setTimeout(r, 400 * (attempt + 1)));
            }
          }
          setProgress({ done: i + 1, total: ids.length });
        }
        setProgress(null);
      },
      said,
    );

  const keepAllFlags = () =>
    decideMany(
      undecided(unwrap(curation.data)).map((i) => i.id),
      'KEEP',
      'Every flag decided KEEP. Nothing is dropped from this package.',
    );

  /**
   * Choose the delivery bucket name.
   *
   * The response carries the name, the title and a timestamp, and nothing
   * about whether the name was free. The backend probes S3's global namespace
   * and advances the ladder only when the name is Available; a taken name
   * leaves the run in NAMING with its reason recorded. So the run's own state
   * after the write is the only honest answer, and reporting "Named" from the
   * fact that the request returned 200 told an operator an irreversible step
   * had happened while nothing had moved.
   */
  const chooseName = async () => {
    const before = state;
    await act(
      'name',
      () => backend.selectPackagingName(runId, bucketName.trim(), title.trim(), newIdempotencyKey()),
      'Name recorded. Checking whether it is free.',
    );
    const after = await qc.fetchQuery({
      queryKey: ['packaging-run', runId],
      queryFn: ({ signal }) => backend.getPackagingRun(runId, signal),
    });
    const moved = (after as { state?: string } | undefined)?.state !== before;
    setNoteFor('name');
    setNoteOk(moved);
    setNote(
      moved
        ? 'Named. Structuring is closed for this run, and the documents are next.'
        : `That name is not free in S3's global namespace, so this run is still waiting for one. Nothing was closed; try a different name.`,
    );
  };

  const act = async (label: string, run: () => Promise<unknown>, said: string, background = false) => {
    setBusy(label);
    setNote(null);
    setNoteFor(label);
    setNoteOk(true);
    try {
      await run();
      setNote(said);
      // A background job has only been ACCEPTED here. Holding it lets the
      // controls keep saying so until the server reports it landed, instead
      // of snapping back to untouched.
      if (background) {
        setWatching(label);
        if (label === 'population') {
          setWatchStartedAt(Date.now());
          setTick(Date.now());
        }
      }
      refresh();
    } catch (e) {
      setNoteOk(false);
      // Read the counter before `finally` clears it. A sequential bulk write
      // that dies partway leaves the run half decided, and the number of
      // writes that landed is the only thing that makes the next press safe
      // to reason about.
      const done = progressRef.current;
      setNote(
        e instanceof ApiError && e.code === 'NOT_FOUND'
          ? 'That step is not deployed on the API yet. It is not needed to continue - carry on with the step below.'
          : e instanceof ApiError && e.code === 'CONFLICT'
            ? `${e.message} This run cannot advance from where it is. Start a new run above; every decision made here stays on the record.`
            : `${explainLine(e, 'change this package')}${
              done ? ` ${done.done.toLocaleString()} of ${done.total.toLocaleString()} were recorded before it stopped; pressing again repeats only what is left.` : ''
            }`,
      );
      refresh();
    } finally {
      setBusy(null);
      setProgress(null);
    }
  };

  const at = (...states: string[]) => states.includes(state);

  // The decision in front of the operator right now, numbered so the page
  // reads as a task with an end rather than a wall of panels.
  const ORDER = ['DISCOVERING', 'AXIS_PENDING', 'STRUCTURING', 'NAMING', 'DOCUMENTING', 'EXPORTABLE'];
  const showGaps = at('EXPORTABLE', 'GATE_PENDING', 'GATE_PASSED', 'GATE_FAILED', 'DELIVERING', 'DELIVERED');

  const toolset = useStep(runId, 'toolset', (s) => backend.getPackagingToolset(runId, s), at('DISCOVERING'));
  // Read at the gate too: the gate's own remedy points at these flags, and
  // an instruction naming a control that is not on the page is worse than no
  // instruction at all.
  const curation = useStep(
    runId,
    'curation',
    (s) => backend.getPackagingCuration(runId, s),
    at('STRUCTURING', 'DOCUMENTING', 'EXPORTABLE', 'GATE_PENDING', 'GATE_FAILED'),
  );
  // Read past STRUCTURING too: the folders are what a buyer receives, and the
  // question "what exactly did I hand over" is asked after the gate, not
  // before it. There was no pathway to this beyond the assembly steps.
  const structure = useStep(
    runId,
    'structure',
    (s) => backend.getPackagingStructure(runId, s),
    at('STRUCTURING', 'NAMING', 'DOCUMENTING', 'EXPORTABLE', 'GATE_PENDING', 'GATE_PASSED', 'DELIVERING', 'DELIVERED'),
  );
  const layout = useStep(runId, 'layout', (s) => backend.getPackagingStructureOptions(runId, s), at('STRUCTURING'));
  const names = useStep(runId, 'names', (s) => backend.getPackagingNameCandidates(runId, s), at('STRUCTURING', 'NAMING'));
  const documents = useStep(runId, 'documents', (s) => backend.getPackagingDocuments(runId, s), at('DOCUMENTING'));

  /**
   * The default name, taken from the answer rather than composed here.
   *
   * The suffix is the backend's to decide: a source name that would exceed
   * S3's 63 characters with it is omitted, and candidate 0 is a company-scoped
   * name instead. Rebuilding the rule here would get that case wrong.
   */
  const proposed = nameCandidates(unwrap(names.data))[0] ?? null;
  useEffect(() => {
    if (proposed && !nameTouched && bucketName === '') setBucketName(proposed);
  }, [proposed, nameTouched, bucketName]);

  const exportsQ = useQueries({
    queries: exportIds.map((id) => ({
      queryKey: ['packaging-export', runId, id],
      queryFn: ({ signal }: { signal?: AbortSignal }) => backend.getPackagingExport(runId, id, signal),
      retry,
      refetchInterval: (q: { state: { data?: unknown } }) =>
        (() => {
          // Both READY and FAILED are terminal. Polling a failed build forever
          // is how it looked like it was still working.
          const st = (unwrap(q.state.data) as { state?: string } | undefined)?.state;
          return st === 'READY' || st === 'FAILED' ? false : 5000;
        })(),
    })),
  });
  // Read wherever the folder tree is drawn, because the tree needs the
  // population criterion to say whether any of it ships. The route computes
  // the checks live from recorded facts and records no transition, so reading
  // it early costs a query and decides nothing.
  const gate = useStep(
    runId,
    'gate',
    (s) => backend.getPackagingGate(runId, s),
    at('STRUCTURING', 'NAMING', 'DOCUMENTING', 'EXPORTABLE', 'GATE_PENDING', 'GATE_PASSED', 'GATE_FAILED', 'DELIVERING', 'DELIVERED'),
    // The gate carries the population criterion, which is the only place the
    // page can see a take-stock finish. Polled while one is being waited on,
    // and left alone the rest of the time.
    watching === 'population' ? 4000 : false,
  );
  // The delivery is the actual output: this package copied into a bucket of
  // its own. It was read only once already in flight, so the step that starts
  // it had nothing to render from and never existed.
  /**
   * The delivery, polled while it is still moving.
   *
   * This is the longest-running thing the product does - every object copied
   * into the buyer's bucket, then read back and compared - and its step said
   * "Progress appears here as objects are uploaded and verified" over a read
   * that was issued once. The counters sat at whatever they were when the step
   * opened, so a copy that was working looked identical to one that had
   * stalled, for as long as the tab stayed open.
   *
   * Stops the moment the record is terminal, so a finished or failed delivery
   * is not polled forever.
   */
  const delivery = useStep(
    runId,
    'delivery',
    (s) => backend.getPackagingDelivery(runId, s),
    at('GATE_PENDING', 'GATE_PASSED', 'DELIVERING', 'DELIVERED', 'DELIVERY_FAILED'),
    DELIVERY_POLL_MS,
  );
  // Whether a delivery would carry anything. The structure tree is the walk's
  // projection; the manifest is CANONICAL dispositions, and before the
  // population is registered the two disagree by tens of thousands of files.
  const ship = shippingState(readGate(unwrap(gate.data)));
  /**
   * The one step to act on. Eight cards used to answer this independently and
   * disagreed; it is answered once and they read from it.
   */
  const gateChecks = readGate(unwrap(gate.data));
  /** How much of the population is in, as a measure rather than a clock. */
  const popProgress = populationProgress(gateChecks);
  /**
   * Working, waiting, or stopped producing anything.
   *
   * The gate's own criterion is the only place a finish is visible, and it
   * reports zero for a job that failed exactly as it does for one that has not
   * started. Elapsed time is what separates them.
   */
  const popVerdict = populationVerdict({
    startedAt: watchStartedAt,
    now: tick,
    registered: popProgress?.registered ?? 0,
  });
  const live = liveStep(state, {
    willShip: ship.willShip,
    gateAllPass: Boolean(gateChecks && gateChecks.checks.every((c) => c.pass)),
    exportsBuilt: exportIds.length > 0,
  });
  const drawn = (id: WizardStep) => cardState(id, live, STEPS);
  /**
   * Whether a step belongs on the page at all.
   *
   * Each card used to mount only in its own run states, so advancing made one
   * vanish and another appear in the same frame - the page rearranging itself
   * around whatever you were reading. A step that has been reached stays, as a
   * collapsed header: a finished one is a record, and the live one is the only
   * card with a body.
   */
  const reached = (id: WizardStep) => STEPS.indexOf(id) <= STEPS.indexOf(live ?? STEPS[STEPS.length - 1]!);
  /** When this run last moved, so a held run's clock measures the real wait. */
  const heldSince = stateChangedAt ? Date.parse(stateChangedAt) : undefined;

  /**
   * A delivery the page can clear is retried here rather than reported.
   *
   * A package that moved between the request and the copy, a destination that
   * could not be written, a source object that could not be read, a worker
   * that died mid-claim: nothing was handed over and nothing was lost, and the
   * next request carries the current manifest. Handing any of them to an
   * operator as an outcome with an instruction attached made a machine's
   * bookkeeping into a person's job, and on a held run the instruction could
   * not even be followed.
   *
   * Which failures those are is `deliveryRetry.ts`'s decision, not this file's.
   * Bounded to three attempts per run, counted in a ref so navigating away and
   * back does not re-arm it, and only while the run can actually accept a
   * request.
   */
  const retried = useRef<Record<string, number>>({});
  const deliveryRecord = unwrap(delivery.data) as PackagingDelivery | null | undefined;
  /**
   * Which failures the page clears itself, decided in `deliveryRetry.ts`.
   *
   * This was an allow-list of codes written out here, and it was wrong four
   * times running - each fix adding whichever code had just been missed. The
   * contract explains why it could not converge: `failureCode` is
   * `type: string` with no enum anywhere, an OPEN set, so a client cannot
   * enumerate it and an allow-list is incomplete by construction.
   *
   * The rule is inverted and it lives in a module with its own tests, because
   * the boundary is the thing that kept moving: two failures must reach a
   * person and everything else is retried, whatever it turns out to be called.
   */
  const failureCode = deliveryRecord?.failureCode ?? null;
  const attempts = retried.current[attemptKey(runId)] ?? 0;
  const canRequestDelivery = state === 'GATE_PASSED';
  const failedOnStaleSnapshot = shouldRetry(deliveryRecord, attempts, {
    canRequest: canRequestDelivery,
    mayDeliver,
  });
  useEffect(() => {
    if (!failedOnStaleSnapshot) return;
    // Keyed on the run alone. With the code in the key, a delivery failing
    // under a different name on each attempt bought itself a fresh three
    // every time, and the executor's codes are exactly what varies.
    const key = attemptKey(runId);
    // Bounded: a cause that survives three attempts is not transient, and
    // repeating a 238 GB copy forever is worse than stopping.
    if ((retried.current[key] ?? 0) >= MAX_ATTEMPTS) return;
    retried.current[key] = (retried.current[key] ?? 0) + 1;
    void act(
      'delivery',
      () => backend.startPackagingDelivery(runId, newIdempotencyKey()),
      'Requested again automatically.',
      true,
    );
    // `act` is redefined every render; the ref is what makes this run once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [failedOnStaleSnapshot, runId, failureCode]);
  /**
   * How long, in words. A minutes-only reading turns a seventeen-hour hold
   * into "1031m", which reads as a stopwatch rather than as a date.
   */
  const heldFor = (() => {
    if (!heldSince) return 'some time';
    const mins = Math.max(0, Math.round((Date.now() - heldSince) / 60000));
    if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'}`;
    const hours = Math.round(mins / 60);
    if (hours < 48) return `${hours} hour${hours === 1 ? '' : 's'}`;
    return `${Math.round(hours / 24)} days`;
  })();
  /**
   * The server's answer is what ends the wait - not a timer, and not the POST
   * having returned. `willShip` flips the moment the population criterion
   * passes, which is exactly what taking stock was for.
   */
  useEffect(() => {
    if (watching === 'population' && ship.willShip) {
      setWatching(null);
      setWatchStartedAt(null);
    }
  }, [watching, ship.willShip]);

  // The clock the stall is measured against. It runs only while a population
  // is being watched and stops the moment the watch ends, so a settled page
  // holds no interval.
  useEffect(() => {
    if (watching !== 'population') return;
    const t = setInterval(() => setTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, [watching]);

  // A job that has registered nothing past the window is not a wait. Ending
  // the watch here is what re-enables the button and stops the poll.
  useEffect(() => {
    if (watching === 'population' && popVerdict.kind === 'stalled') setWatching(null);
  }, [watching, popVerdict.kind]);
  /**
   * The delivery's own record is what ends this wait: once it exists the
   * counters below ARE the progress, and holding the button in a spinner on
   * top of them would be two claims about the same work.
   */
  useEffect(() => {
    if (watching === 'delivery' && unwrap(delivery.data)) setWatching(null);
  }, [watching, delivery.data]);

  const needs: Record<string, string> = {
    DISCOVERING: 'Choose which tools ship. Everything the sealed walk found is listed below.',
    AXIS_PENDING: 'Choose how the package is organised. This decides the folder tree.',
    STRUCTURING: 'Review what was flagged, then name the package. Naming is what moves it on.',
    DOCUMENTING: 'Read each drafted document and accept it. Accepting all of them unlocks the exports.',
    EXPORTABLE: 'Build both files, download them, then run the release checks.',
    GATE_PENDING: `The gate has been evaluated. Read its criteria below: any that are not met name what to do, and the ones that pass are your evidence. Handing it over is step ${stepNo('deliver')}.`,
    // No duration here either: the copy begins when a worker claims the
    // delivery, and this build has never measured how long that takes.
    GATE_PASSED: `The gate passed. Step ${stepNo('deliver')} copies this package into a bucket of its own; the copy begins when a worker claims it, and step ${stepNo('deliver')} counts what has actually been copied.`,
    GATE_FAILED: 'The gate refused this run. Start a new one; every decision made here stays on the record.',
    DELIVERY_FAILED: 'Delivery failed on this run. Start a new one.',
  };

  const gaps = gapSummary(unwrap(discovery?.data));

  return (
    <>
      {gaps && showGaps && (
        <Panel>
          <PanelHeader
            title="Problems found"
            sub="anything unreadable, unrecognised or self-contradictory, each one counted"
          />
          <div className="flex flex-wrap gap-x-8 gap-y-2 px-4 py-3">
            {([
              ['open', gaps.open, (gaps.open ?? 0) > 0 ? 'warn' : 'mut'],
              ['blocking', gaps.blockingOpen, (gaps.blockingOpen ?? 0) > 0 ? 'bad' : 'mut'],
              ['acknowledged', gaps.acknowledged, 'mut'],
              ['resolved', gaps.resolved, 'mut'],
            ] as const).map(([label, n, tone]) => (
              <span key={label} className="flex flex-col">
                <span
                  className={
                    'mono text-stat ' +
                    (tone === 'bad' ? 'text-bad' : tone === 'warn' ? 'text-warn' : 'text-ink-2')
                  }
                >
                  {count(n ?? undefined)}
                </span>
                <span className="text-caption text-ink-3">{label}</span>
              </span>
            ))}
          </div>
          {(gaps.open ?? 0) > 0 && (
            <p className="border-t border-line px-4 py-2 text-caption text-ink-3">
              These do not stop you shipping. They travel with the package so whoever receives it knows exactly what
              could not be read, before they find the absence themselves.
            </p>
          )}
        </Panel>
      )}

      {needs[state] && !ORDER.includes(state) && (
        <p className="rounded-md border border-line bg-panel px-4 py-2.5 text-small text-ink-2">
          <span className="text-caption font-semibold uppercase tracking-wider text-ink-3">Now</span>{' '}
          {needs[state]}
        </p>
      )}
      {/* Every outcome reaches a screen. ActionNote renders beside the control
          that caused it, which is right for the five that have one; act() sets
          noteFor for ten labels, so the other five reported nothing at all.
          A rejected placement rule, a 503 forty files into a ninety-nine-file
          drop and the CONFLICT sentence written for exactly this all rendered
          as the busy strip vanishing. The catch-all fires whenever the label
          has no inline slot, rather than only when there is no label. */}
      {note && !INLINE_NOTE.has(noteFor ?? '') && (
        <p
          role="status"
          className={cn(
            'rounded border px-3 py-2 text-small',
            noteOk ? 'border-ok/40 bg-ok-soft text-ink-2' : 'border-warn/40 bg-warn-soft text-ink-2',
          )}
        >
          {note}
        </p>
      )}

      {/* A disabled control with no reason beside it reads as a broken
          product. This says it once, at the top, rather than repeating the
          same sentence against eight buttons. */}
      {!mayOperate && (
        <p className="rounded border border-line-2 bg-panel px-3 py-2 text-small text-ink-2">
          {cannotBecause(user?.role, 'operate')} Everything on this page is readable, and the controls that change the
          package are closed to your account.
        </p>
      )}

      {/* Packaging persistence refuses concurrent writes, so one running action
          locks every control on the page. That lock is correct and it was
          silent: eight buttons greyed out for up to a minute with nothing
          saying why, which reads as a broken screen rather than a busy one. */}
      {busy !== null && (
        <p role="status" className="flex flex-wrap items-center gap-2 rounded border border-line-2 bg-panel px-3 py-2 text-small text-ink-2">
          <span className="h-1.5 w-1.5 flex-none animate-pulse rounded-full bg-acc" aria-hidden="true" />
          <span>
            {WORKING[busy] ?? 'Saving your decision'}
            {progress ? ` · ${progress.done.toLocaleString()} of ${progress.total.toLocaleString()}` : ''}
          </span>
          <span className="text-caption text-ink-3">
            The other controls wait until this finishes, because this run takes one change at a time.
          </span>
        </p>
      )}

      {reached('include') && (
        <StepCard
          step={stepNo('include')}
          summary="Chosen. What ships and how strays are handled is recorded on this run."
          of={STEP_COUNT}
          state={drawn('include')}
          title="Choose what goes in"
          /* The instruction described the successful path and sat above a
             sentence saying this run cannot take it, so the card opened by
             telling an operator what happens next and then told them nothing
             here moves it forward. One card, one situation. */
          instruction={
            toolset.isSuccess && toolIds(unwrap(toolset.data)).length === 0
              ? 'This walk traced no file to any software, and a package cannot be built from nothing. What changes this is another walk, or a new run once one has improved the classification.'
              : 'Everything found in this bucket ships by default, with the untraced files in a folder of their own. Leaving a tool out never deletes anything at source. Next you will choose how the folders are arranged.'
          }
        >
          {/* The only exit from DISCOVERING is a toolset selection, and the
              backend refuses an empty one. A pending or failed read, or a walk
              that traced nothing, left this button greyed with no message: the
              run is remembered across reloads, so the operator came back to the
              same dead card forever. */}
          {toolset.isPending && (
            <p className="px-4 pt-3 text-small text-ink-3">Reading the tools this walk traced.</p>
          )}
          {toolset.isError && (
            <p className="px-4 pt-3 text-small text-warn">
              {explainLine(toolset.error, 'read the tools found in this bucket')}{' '}
              <button
                onClick={() => void toolset.refetch()}
                className="underline underline-offset-2 hover:text-ink"
              >
                Try again
              </button>
            </p>
          )}
          {/* A control that cannot work is worse than no control. With nothing
              traced, the only honest move is off this card, so that is what is
              offered instead of a disabled button under a sentence explaining
              why it will not work. */}
          {toolset.isSuccess && toolIds(unwrap(toolset.data)).length === 0 ? (
            <div className="flex flex-wrap items-center gap-3 px-4 py-3">
              <Link
                to={`/buckets/${bucketId}`}
                className="rounded border border-line-2 bg-panel px-3 py-1.5 text-small font-medium text-acc-2 transition-colors duration-fast hover:bg-hov"
              >
                Go back and walk this bucket
              </Link>
              <span className="text-small text-ink-2">
                This run stays on the record, and the decisions made on it are kept.
              </span>
            </div>
          ) : (
          <div className="flex flex-wrap items-center gap-3 px-4 py-3">
            <Button
              loading={busy === 'toolset'}
              disabled={live !== 'include' || !mayOperate || busy !== null}
              onClick={() =>
                void act(
                  'toolset',
                  () =>
                    backend.selectPackagingToolset(
                      runId,
                      toolIds(unwrap(toolset.data)),
                      { mode: 'OWN_FOLDER' },
                      newIdempotencyKey(),
                    ),
                  'Every tool ships; the unattributed remainder ships in its own folder.',
                )
              }
            >
              {busy === 'toolset' ? 'Recording…' : 'Ship every tool'}
            </Button>
                <ActionNote when="toolset" noteFor={noteFor} note={note} ok={noteOk} />
            <span className="text-small text-ink-2">
              you can change this later; each choice replaces the last and both stay on the record
            </span>
          </div>
          )}
        </StepCard>
      )}

      {/* guard_packaging_axis_decision accepts AXIS_PENDING and STRUCTURING,
          so the layout stays revisable while the structure is being worked on.
          Offering it in the first state only made a reconsidered layout cost a
          whole new run, for no reason the database imposes. */}
      {/* Registering the population is the step that turns walk facts into the
          manifest, and the database accepts it in STRUCTURING and nowhere
          else. It used to sit in step 1, where every press is refused for the
          run state, and it was described as optional. Both are why runs
          reached the gate with an empty package and could still request a
          delivery of it. */}
      {/* The one required move at STRUCTURING, and it was the only step in
          the ladder drawn as a plain panel: a grey header among gold-bordered
          step cards, which reads as a note about the run rather than the thing
          the banner just told you to do. */}
      {/* Step 2 before step 3. The layout Details rendered a hundred lines
          below the take-stock card, so a wizard that numbers its steps drew
          3 above 2 - and an operator reading down the page met them out of
          order with nothing saying why. */}
      {at('STRUCTURING') && (
        <Details title={`Step ${stepNo('layout')} of ${STEP_COUNT} · Change the layout - already chosen, and still changeable`}>
          <div className="px-4 py-3">
            <p className="mb-2 text-small text-ink-2">
              Choosing again replaces the previous choice and rebuilds the tree; it is recorded as a decision rather
              than an edit.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              {AXES.map((axis) => (
                <Button
                  key={axis}
                  variant="ghost"
                  disabled={live !== 'layout' || busy !== null}
                  loading={busy === axis}
                  onClick={() =>
                    void act(
                      axis,
                      () => backend.selectPackagingAxis(runId, axis, newIdempotencyKey()),
                      `Folders will be grouped by ${AXIS_LABEL[axis] ?? say(axis)}.`,
                    )
                  }
                >
                  {busy === axis ? 'Selecting…' : (AXIS_LABEL[axis] ?? say(axis))}
                </Button>
              ))}
            </div>
          </div>
        </Details>
      )}

      {reached('stock') && (
        <div className="mb-3">
          <StepCard
            step={stepNo('stock')}
            summary={ship.willShip ? 'Every file has a manifest line.' : 'Not yet taken; nothing can ship until it is.'}
            of={STEP_COUNT}
            // Registered, so this is history and naming is the move.
            state={drawn('stock')}
            title="Take stock of every file"
            instruction={
              ship.willShip
                ? `${ship.detail ?? 'The population is registered.'} The package has a manifest, so there is something to deliver.`
                : 'This writes one manifest line per file, filed under the software that made it or under the reason it could not be traced. Until it runs the package is empty and there is nothing to hand over. Naming the package moves this run past the point where it can be done, and only a new run can get back here. It runs in the background and is safe to repeat.'
            }
            action={
              <>
                <Button
                  disabled={live !== 'stock' || !mayOperate || busy !== null || watching !== null || ship.willShip}
                  loading={busy === 'population' || watching === 'population'}
                  onClick={() =>
                    void act(
                      'population',
                      () => backend.registerPackagingPopulation(runId, newIdempotencyKey()),
                      'Started. It writes one line per file in the background, so a large bucket takes a while; this page keeps watching.',
                      true,
                    )
                  }
                >
                  {busy === 'population' || watching === 'population'
                    ? 'Taking stock…'
                    : ship.willShip
                      ? 'Already registered'
                      : 'Take stock of every file'}
                </Button>
                <ActionNote when="population" noteFor={noteFor} note={note} ok={noteOk} />
                {/* A dispatch that produced nothing. The page cannot say why -
                    the refusal happens in the executor and reaches no route it
                    can read - so it states what it measured and names the run,
                    which is what finds it in the log. */}
                {popVerdict.kind === 'stalled' && (
                  <p
                    role="alert"
                    className="mt-1.5 rounded border border-warn bg-warn-soft px-3 py-2 text-small text-ink-2"
                  >
                    {stalledSentence(popVerdict.elapsedMs, runId)}
                  </p>
                )}
                {(busy === 'population' || watching === 'population') && (
                  <>
                    {/*
                      * Measured, not timed.
                      *
                      * This was a spinner and an elapsed clock, and a clock
                      * answers the wrong question - it says how long you have
                      * waited, never how much is left. "4m 41s" against "0
                      * records registered" cannot distinguish a job grinding
                      * through 42,247 objects from one that refused at the
                      * first. The "usually takes a few minutes" beside it was
                      * never measured; it was inherited copy, and a system
                      * tool does not get to guess at a duration and print it
                      * as though it were observed.
                      */}
                    {popProgress && (
                      <div className="mt-1.5" aria-live="polite">
                        <p className="mono text-caption text-ink-2">
                          {popProgress.registered.toLocaleString()} of {popProgress.total.toLocaleString()} files registered
                          <span className="ml-2 text-ink-4">
                            {popProgress.remaining.toLocaleString()} remaining · {popProgress.percent}%
                          </span>
                        </p>
                        <div className="mt-1 h-1 w-full overflow-hidden rounded bg-panel-2">
                          <div className="h-full bg-acc" style={{ width: `${popProgress.percent}%` }} />
                        </div>
                      </div>
                    )}
                    {/*
                      * The count, from the criterion that decides this step.
                      *
                      * A spinner and "Started." say a job exists. They do not
                      * say whether it is moving, and on a bucket where the
                      * walk covered nothing the honest answer is that it is
                      * not - which the operator could only discover by giving
                      * up. The gate is already polled every four seconds while
                      * this is watched; its own sentence carries the number
                      * ("N records registered, M without a current
                      * disposition"), so the number is shown rather than
                      * hidden behind the spinner that was waiting on it.
                      */}
                    {/* The backend's own sentence, when the two counts did
                        not parse - its words rather than a number invented
                        here. */}
                    {!popProgress && ship.detail && (
                      <p className="mono mt-1 text-caption text-ink-2" aria-live="polite">
                        {ship.detail}
                      </p>
                    )}
                    <p className="mt-1 text-caption text-ink-4">Re-read every 4 seconds; this changes on its own.</p>
                  </>
                )}
              </>
            }
          />
        </div>
      )}

      {at('AXIS_PENDING') && (
        <StepCard
          step={stepNo('layout')}
          of={STEP_COUNT}
          state={drawn('layout')}
          title={at('STRUCTURING') ? 'Change the layout' : 'Choose the layout'}
          instruction={
            at('STRUCTURING')
              ? 'The folders can still be reorganised while you are structuring. Choosing again replaces the previous choice and rebuilds the tree; it is recorded as a decision rather than an edit.'
              : 'How the folders are organised for whoever receives this. Product gives one folder per piece of software and is the usual choice. Next you will name the package.'
          }
        >
          {/* Three identical grey buttons, and the recommendation named only
              in the paragraph above them. The instruction says product is the
              usual choice; the control it refers to looked exactly like the
              two that are not. */}
          <div className="flex flex-wrap items-center gap-2 px-4 py-3">
            {AXES.map((axis) => (
              <Button
                key={axis}
                variant={axis === RECOMMENDED_AXIS ? 'primary' : 'ghost'}
                disabled={busy !== null}
                loading={busy === axis}
                onClick={() =>
                  void act(
                    axis,
                    () => backend.selectPackagingAxis(runId, axis, newIdempotencyKey()),
                    `Folders will be grouped by ${AXIS_LABEL[axis] ?? say(axis)}.`,
                  )
                }
              >
                {busy === axis
                  ? 'Selecting…'
                  : `${AXIS_LABEL[axis] ?? say(axis)}${axis === RECOMMENDED_AXIS ? ' · usual choice' : ''}`}
              </Button>
            ))}
          </div>
        </StepCard>
      )}

      {/* Shown wherever flags are still undecided, not only at STRUCTURING: the
          gate blocks on exactly this and its remedy has to point somewhere. */}
      {(at('STRUCTURING') || undecided(unwrap(curation.data)).length > 0) && (
        <>
          <Details title={`Worth leaving out? ${undecided(unwrap(curation.data)).length} still to decide - keeping everything is the default`}>
            {(() => {
              const flags = readFlags(unwrap(curation.data));
              return flags ? (
                <CurationFlags
                  flags={flags}
                  busyId={busy?.startsWith('flag:') ? busy.slice(5) : null}
                  onDecide={(flagId, decision) =>
                    void act(
                      `flag:${flagId}`,
                      () => backend.decidePackagingCuration(runId, flagId, decision, newIdempotencyKey()),
                      decision === 'DROP'
                        ? 'Left out of this package. It is recorded as an exclusion, so the counts still add up.'
                        : 'Kept in this package.',
                    )
                  }
                  busy={!mayOperate || busy !== null}
                  onDecideMany={(ids, decision) =>
                    void decideMany(
                      ids,
                      decision,
                      decision === 'DROP'
                        ? `${ids.length} ${ids.length === 1 ? 'flag' : 'flags'} left out. Each one covers every file it groups, and they are recorded as exclusions so the counts still add up.`
                        : `${ids.length} files kept in this package.`,
                    )
                  }
                />
              ) : (
                <Facts value={unwrap(curation.data)} pending={curation.isPending} error={curation.error} />
              );
            })()}
            <div className="flex flex-wrap items-center gap-3 border-t border-line px-4 py-3">
              <Button
                disabled={!mayOperate || busy !== null || undecided(unwrap(curation.data)).length === 0}
                onClick={() => void keepAllFlags()}
              >
                {busy === 'keep-all'
                  ? progress
                    ? `Keeping ${progress.done} of ${progress.total}…`
                    : 'Keeping…'
                  : `Keep all ${undecided(unwrap(curation.data)).length}`}
              </Button>
              <span className="min-w-0 flex-1 text-small text-ink-2">
                {proposedDrops(unwrap(curation.data)) > 0
                  ? `We would suggest leaving out ${proposedDrops(unwrap(curation.data))} of these. Keeping them all ships every file; nothing is excluded. Each one is recorded separately, so this takes about a minute and the button counts as it goes. Leaving something out is a deliberate choice, one at a time.`
                  : 'Keeping them all ships every file.'}
              </span>
            </div>
          </Details>
          <Details title="How the folders are laid out - choose the grouping per tool">
            {(() => {
              const layouts = readLayouts(unwrap(layout.data));
              return layouts ? (
                <FolderLayout
                  layouts={layouts}
                  // Its own contract disables on a non-null busy, so a role
                  // that cannot operate is passed as a running write rather
                  // than by editing a component another session holds.
                  busy={mayOperate ? busy : 'role'}
                  onChoose={(groupPath, level3, leafOrder) =>
                    void act(
                      `${groupPath}:${level3}`,
                      () => backend.sortPackagingStructure(runId, groupPath, level3, leafOrder, newIdempotencyKey()),
                      'Layout recorded.',
                    )
                  }
                />
              ) : (
                <Facts value={unwrap(structure.data)} pending={structure.isPending} error={structure.error} />
              );
            })()}
          </Details>
          {/* The folders the buyer will open. The structure route has always
              returned the built tree and the page only ever showed the chips
              that produce it. */}
          <Details title="The folders this will produce">
            {(() => {
              const built = readTree(unwrap(structure.data));
              return built ? (
                <PackageTree tree={built.tree} unassigned={built.unassigned} truncated={built.truncated} shipping={ship} />
              ) : (
                <Facts value={unwrap(structure.data)} pending={structure.isPending} error={structure.error} />
              );
            })()}
          </Details>
          <Details title="Send a tool's files somewhere else">
            <PlacementRules
              tools={toolIds(unwrap(toolset.data))}
              rules={readRules(unwrap(structure.data))}
              busy={!mayOperate || busy !== null}
              onAdd={(rule) =>
                void act(
                  'rule',
                  () => backend.mutatePackagingPlacementRule(runId, rule, newIdempotencyKey()),
                  'Rule recorded; the folder tree above is rebuilt from it.',
                )
              }
            />
          </Details>
        </>
      )}

      {reached('name') && (
        <StepCard
          step={stepNo('name')}
          summary={at('DOCUMENTING', 'EXPORTABLE', 'GATE_PENDING', 'GATE_PASSED', 'DELIVERING', 'DELIVERED') ? 'Named. Structuring is closed.' : 'Not yet named.'}
          of={STEP_COUNT}
          // Closed until the population is registered; the live step after it.
          state={drawn('name')}
          title="Name it"
          instruction={
            !ship.known
              ? 'Checking whether this package holds anything. Naming stays closed until that comes back, because naming closes structuring for good and a run named while empty can never hold a file.'
              : !ship.willShip
                ? 'Take stock of every file first. Naming closes structuring, and structuring is the only state that accepts a population, so naming now would leave this run permanently unable to hold a file.'
                : 'Give the package a name and a title. Naming finishes the structure too, so the next screen is the documents. That jump is expected.'
          }
        >
          <div className="flex flex-wrap items-center gap-2 px-4 py-3">
            {nameCandidates(unwrap(names.data)).length > 0 && (
              <select
                value=""
                onChange={(e) => {
                  if (!e.target.value) return;
                  setNameTouched(true);
                  setBucketName(e.target.value);
                }}
                aria-label="Use a proposed name"
                className="rounded border border-line bg-panel-2 px-2 py-1 text-small text-ink"
              >
                <option value="">other proposed names…</option>
                {nameCandidates(unwrap(names.data)).map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            )}
            <input
              value={bucketName}
              onChange={(e) => {
                setNameTouched(true);
                setBucketName(e.target.value);
              }}
              placeholder="delivery bucket name"
              className="mono w-64 rounded border border-line bg-panel-2 px-2 py-1 text-small text-ink"
            />
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="package title"
              className="w-72 rounded border border-line bg-panel-2 px-2 py-1 text-small text-ink"
            />
            <Confirm
              label="Use this name"
              confirmLabel={`Name it ${bucketName.trim() || 'this'}`}
              consequence="This closes structuring for good. Nothing can be added to the package afterwards."
              busyLabel="Naming…"
              loading={busy === 'name'}
              disabled={live !== 'name' || 
                !mayOperate ||
                busy !== null ||
                bucketName.trim().length < 3 ||
                title.trim().length === 0 ||
                // Fail-closed: an unread gate is not permission. See
                // namingAllowed for why this is the one control that must
                // refuse on unknown rather than allow.
                !namingAllowed(ship)
              }
              onConfirm={() => void chooseName()}
            />
            <ActionNote when="name" noteFor={noteFor} note={note} ok={noteOk} />
          </div>
        </StepCard>
      )}

      {reached('documents') && (
        <StepCard
          step={stepNo('documents')}
          summary="The package's own documents."
          of={STEP_COUNT}
          state={drawn('documents')}
          title="Read and approve the documents"
          /* The count was written into the sentence: "Four documents… accepting
             all four unlocks the files". The backend drafts what this run needs
             and it is not always four, so the page stated a number it had not
             read, next to the list that contradicts it. */
          instruction={(() => {
            const all = drafts(unwrap(documents.data));
            const left = all.filter((a) => a.state !== 'ACCEPTED').length;
            if (documents.isPending || all.length === 0)
              return 'Documents describing this package, written from its own figures. Read each one and accept it. Accepting all of them unlocks the files to hand over.';
            return `${all.length === 1 ? 'One document describes' : `${all.length} documents describe`} this package, written from its own figures. Read each one and accept it. ${
              left === 0 ? 'All of them are accepted.' : `${left} still ${left === 1 ? 'needs' : 'need'} accepting, and the last one unlocks the files to hand over.`
            }`;
          })()}
        >
          {/* DOCUMENTING advances only by accepting artifacts and there is no
              way back, so a failed or unrecognised read was a dead end drawn as
              an empty list. */}
          {documents.isPending ? (
            <p className="px-4 py-3 text-small text-ink-3">Reading the drafted documents.</p>
          ) : documents.isError ? (
            <p className="px-4 py-3 text-small text-warn">
              {explainLine(documents.error, 'read the drafted documents')}{' '}
              <button
                onClick={() => void documents.refetch()}
                className="underline underline-offset-2 hover:text-ink"
              >
                Try again
              </button>
            </p>
          ) : drafts(unwrap(documents.data)).length === 0 ? (
            <p className="px-4 py-3 text-small text-warn">
              No documents came back for this run. Accepting them is the only way out of this step, so nothing here can
              move it forward; start a new run for this bucket.
            </p>
          ) : (
            drafts(unwrap(documents.data)).map((a) => (
              <div key={a.key} className="border-b border-line px-4 py-3 last:border-0">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="mono text-small text-ink">{a.fileName}</span>
                  <span className="text-caption text-ink-4">version {a.version} · {a.state.toLowerCase()}</span>
                  <span className="ml-auto">
                    {a.state === 'ACCEPTED' ? (
                      <Button variant="ghost" disabled>
                        accepted
                      </Button>
                    ) : (
                      /* Accepting is one way: DOCUMENTING leaves for
                         EXPORTABLE only by accepting, and no transition comes
                         back. It was a single ghost press. */
                      <Confirm
                        label="Accept"
                        confirmLabel={`Accept ${a.fileName}`}
                        consequence={`${a.fileName} is fixed as the buyer's copy of this document, and this run has no way back to editing it.`}
                        disabled={live !== 'documents' || !mayReview || busy !== null}
                        loading={busy === a.key}
                        busyLabel="Accepting…"
                        onConfirm={() =>
                          void act(
                            a.key,
                            () =>
                              backend.acceptPackagingArtifact(runId, a.key, a.version, a.content, newIdempotencyKey()),
                            `Accepted ${a.fileName}.`,
                          )
                        }
                      />
                    )}
                  </span>
                </div>
                <pre className="mono mt-2 max-h-40 overflow-auto rounded border border-line bg-panel-2 p-2 text-caption text-ink-3">
                  {a.content.slice(0, 1200)}
                </pre>
              </div>
            ))
          )}
        </StepCard>
      )}



      {at('EXPORTABLE', 'GATE_PENDING', 'GATE_PASSED', 'DELIVERING', 'DELIVERED') && (
        <Panel>
          <PanelHeader
            title="What the buyer opens"
            sub={
              ship.known && !ship.willShip
                ? 'the folders this package would hand over · nothing is placed yet, so none of it ships'
                : 'the folders exactly as they will arrive · walk it before you hand it over, and after'
            }
          />
          {(() => {
            const built = readTree(unwrap(structure.data));
            return built ? (
              <PackageTree tree={built.tree} unassigned={built.unassigned} truncated={built.truncated} shipping={ship} />
            ) : (
              <Facts value={unwrap(structure.data)} pending={structure.isPending} error={structure.error} />
            );
          })()}
          {/* The folders above are the walk's projection. This is the delivery
              manifest: the same rows the executor copies, opened file by file,
              so the last look before a handover happens in the product. */}
          <Details title="Every file that ships, one by one">
            <DeliveredFiles runId={runId} />
          </Details>
        </Panel>
      )}

      {at('EXPORTABLE', 'GATE_PENDING', 'GATE_PASSED', 'DELIVERING', 'DELIVERED') && (
        <Panel>
          <PanelHeader
            title={`Step ${stepNo('files')} of ${STEP_COUNT} · The files to hand over`}
            sub="a spreadsheet and a document describing the package · verified on the way out"
          />
          {exportIds.length === 0 ? (
            <p className="px-4 py-3 text-small text-ink-3">
              No export has been built for this run yet. Build one and it appears here to download.
            </p>
          ) : (
            exportsQ.map((q, i) => {
              const e = unwrap(q.data) as
                | { id?: string; kind?: string; state?: string; byteLength?: number; sha256?: string }
                | undefined;
              const id = exportIds[i]!;
              // Nothing deletes an export, so building twice leaves two rows
              // forever. The newest of a kind is the one to hand over; the rest
              // are named as earlier copies rather than sitting there identical.
              const sameKind = exportsQ
                .map((x) => unwrap(x.data) as { id?: string; kind?: string } | undefined)
                .filter((x) => x?.kind && x.kind === e?.kind);
              const isLatest = sameKind.length <= 1 || sameKind[sameKind.length - 1]?.id === (e?.id ?? id);
              // packaging_exports.state is READY or FAILED, and nothing else.
              // AVAILABLE belongs to name-candidate availability, a different
              // enum that happens to share a word; accepting it here was a
              // conflation. FAILED was handled nowhere, so a build that failed
              // sat at "building…" and polled forever.
              const ready = e?.state === 'READY';
              const failed = e?.state === 'FAILED';
              return (
                <div key={id} className="flex flex-wrap items-center gap-3 border-b border-line px-4 py-2.5 text-small last:border-0">
                  {/* "document" told nobody what the file is. Name the thing
                      a buyer receives, not the enum that classifies it. */}
                  <span className="min-w-0 flex-1">
                    <span className="block text-ink">
                      {e?.kind === 'SHEET'
                        ? 'Spreadsheet: every file in the package, one row each'
                        : e?.kind === 'DOCUMENT'
                          ? 'Document: what this package contains and how it was assembled'
                          : (e?.kind ?? 'export').toLowerCase()}
                    </span>
                    <span className={cn('mono block text-caption', failed ? 'text-warn' : 'text-ink-3')}>
                      {ready
                        ? 'ready to download'
                        : failed
                          ? 'the backend could not build this one; build it again'
                          : (e?.state ?? (q.isPending ? 'reading…' : 'building…'))}
                      {typeof e?.byteLength === 'number' ? ` · ${formatBytes(e.byteLength)}` : ''}
                      {!isLatest ? ' · an earlier copy, kept because nothing deletes an export' : ''}
                    </span>
                  </span>
                  <Button
                    variant="ghost"
                    className="ml-auto"
                    disabled={!ready || busy !== null}
                    onClick={() =>
                      void act(
                        `dl-${id}`,
                        async () => {
                          const { blob, filename } = await backend.downloadPackagingExport(id);
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = filename;
                          document.body.appendChild(a);
                          a.click();
                          a.remove();
                          URL.revokeObjectURL(url);
                        },
                        'Downloaded. The API re-hashed the bytes on the way out and they matched.',
                      )
                    }
                  >
                    {busy === `dl-${id}` ? 'Downloading…' : ready ? 'Download' : 'building…'}
                  </Button>
                </div>
              );
            })
          )}
          <div className="flex flex-wrap items-center gap-2 border-t border-line px-4 py-3">
            {(['SHEET', 'DOCUMENT'] as const).filter((kind) => {
              // Only offer to build what is missing. Offering "rebuild" beside
              // a finished file invites building the same thing forever, and
              // there is no route that removes the copies it leaves behind.
              // Offer only what is missing, and re-offer anything that failed.
              const built = exportsQ
                .map((x) => unwrap(x.data) as { kind?: string; state?: string } | undefined)
                .some((x) => x?.kind === kind && x.state === 'READY');
              return !built;
            }).map((kind) => {
              // Both buttons read "Build" whatever had already been built, so
              // there was no way to tell a finished artifact from a missing one
              // and nothing stopped an operator building the same file forever.
              return (
              <Button
                key={kind}
                variant="ghost"
                disabled={live !== 'files' || busy !== null}
                onClick={() =>
                  void act(
                    kind,
                    async () => {
                      const created = (await backend.startPackagingExport(runId, kind, newIdempotencyKey())) as {
                        data?: { id?: string };
                        id?: string;
                      };
                      const id = created?.id ?? created?.data?.id;
                      if (typeof id === 'string') onExportCreated(id);
                    },
                    `${kind.toLowerCase()} export requested; it appears above when the backend finishes building it.`,
                  )
                }
              >
                {busy === kind ? 'Building…' : `Build the ${kind === 'SHEET' ? 'spreadsheet' : 'document'}`}
              </Button>
              );
            })}
            <span className="w-full text-small text-ink-2">
              {(() => {
                const kinds = exportsQ
                  .map((x) => unwrap(x.data) as { kind?: string; state?: string } | undefined)
                  .filter((x) => x?.state === 'READY')
                  .map((x) => x?.kind);
                const have = [kinds.includes('SHEET') && 'spreadsheet', kinds.includes('DOCUMENT') && 'document'].filter(Boolean);
                if (have.length === 2) return 'Both files are built and listed above. Rebuilding replaces neither; it adds another copy.';
                if (have.length === 1) return `The ${have[0]} is built and listed above. The other has not been built yet.`;
                return 'Neither file has been built yet. Both are listed above once they are.';
              })()}
            </span>
          </div>
        </Panel>
      )}

      {/* The evaluation insert is guarded to GATE_PENDING and GATE_PASSED, and
          advance_to only walks the ladder as far as GATE_PENDING. From
          GATE_FAILED, DELIVERING or DELIVERED there is no rung to climb, so the
          button was rendered in three states where every press is refused. */}
      {at('EXPORTABLE', 'GATE_PENDING', 'GATE_PASSED', 'GATE_FAILED', 'DELIVERING', 'DELIVERED') && (
        <StepCard
          step={stepNo('gate')}
          summary="The checks that decide whether this may be handed over."
          of={STEP_COUNT}
          state={drawn('gate')}
          title="Check it is fit to hand over"
          instruction={(() => {
            const g = readGate(unwrap(gate.data));
            /* Written when the gate WAS the last step. Delivery exists, and
               step 8 sits directly beneath this card offering it, so the page
               declared the journey over one paragraph above the control that
               continues it. */
            if (!g)
              return `Run the checks and read what they say. Passing them is what allows step ${stepNo('deliver')}. Sealing writes an evidence link for every admissible signal, so on a package this size the press takes about a minute to answer.`;
            const failed = g.checks.filter((c) => !c.pass);
            if (failed.length === 0)
              return at('GATE_PENDING')
                ? // Passing and advancing are two different things, and only
                  // the second releases a delivery. Evaluating this run
                  // answers 200 with passed: true and leaves it at
                  // GATE_PENDING, so the operator presses Check again, is told
                  // "Checked.", and watches nothing happen - with a delivery
                  // queued behind a transition that is not coming. Silence
                  // here reads as a slow backend; it is a stuck one.
                  (g.sealRefusal
                    ? `Every criterion passes and this run has not been released. Passing is not releasing: the ledger has to record the verdict, and it refused to. ${g.sealRefusal}`
                    : // No refusal came back with this evaluation, which the GET
                      // does not carry. Pressing records one, and the answer to
                      // "why is it held" arrives with it.
                      `Every criterion passes and this run has not advanced. Passing is not releasing: only a verdict the ledger records moves a run on, and this one has not been recorded. Press Check again to record an evaluation; the reason it is refused comes back with it.`)
                : `Every check is met. Your files are above and ready to download, and step ${stepNo('deliver')} copies the whole package into a bucket of its own.`;
            return `${failed.length} of ${g.checks.length} checks are not met. Each one below has the button that clears it. Nothing else on this page needs your attention.`;
          })()}
          action={
            <>
              <Button
                disabled={live !== 'gate' || 
                  !mayOperate ||
                  busy !== null ||
                  !at('EXPORTABLE', 'GATE_PENDING', 'GATE_PASSED')
                }
                onClick={() =>
                  void act(
                    'gate',
                    () => backend.evaluatePackagingGate(runId, newIdempotencyKey()),
                    // Not "sealed": that is the run advancing, which this
                    // response does not prove. Saying it here is the same
                    // mistake as every other place on this page that reported
                    // an accepted request as finished work.
                    'Checked. If the run advances, this card and the delivery below both change on their own.',
                  )
                }
              >
                {busy === 'gate'
                  ? 'Sealing…'
                  : readGate(unwrap(gate.data))
                    ? 'Check again'
                    : 'Run the checks'}
              </Button>
                <ActionNote when="gate" noteFor={noteFor} note={note} ok={noteOk} />
                {/*
                  * Sealing is slow and that is not a fault.
                  *
                  * The press writes an evidence link per admissible signal -
                  * on this package close to a million - so it returns in about
                  * a minute rather than instantly. Without this the one
                  * remaining control in the product looks hung at exactly the
                  * moment it is working, which is the mistake this page has
                  * made in three other places already.
                  */}
                {busy === 'gate' && (
                  <Working
                    what="Sealing the release: writing an evidence link for every admissible signal"
                    usually="a package this size usually takes about a minute"
                    overAt={180}
                  />
                )}
                {/*
                  * A held run is a wait, and a wait with nothing on screen is
                  * indistinguishable from a dead page. This page already polls
                  * the run every six seconds; it simply never said so, so an
                  * operator watching a static card had no way to know anything
                  * was being checked and pressed again instead.
                  */}
                {busy !== 'gate' && at('GATE_PENDING') && readGate(unwrap(gate.data))?.checks.every((c) => c.pass) && (
                  /*
                   * A hold is not an operation in flight.
                   *
                   * This drew a spinner and a running clock, and on a run held
                   * since the previous day it read "1031m 24s" beside the word
                   * waiting - seventeen hours presented as something actively
                   * working. A spinner is a claim that a machine is doing
                   * something right now. Nothing is. So the duration is stated
                   * as a fact about how long the release has been held, in
                   * hours where hours is what it is, and nothing spins.
                   */
                  <p role="status" className="mt-1 text-caption text-warn">
                    Held for {heldFor}. Every criterion passes and the release has not been granted. This page re-reads
                    it every 6 seconds and will change on its own; nothing here needs pressing.
                  </p>
                )}
              <span className="min-w-0 flex-1 text-small text-ink-2">
                {!at('EXPORTABLE', 'GATE_PENDING', 'GATE_PASSED')
                  ? 'This run has left the states where the checks can be recorded. What is below is the last evaluation that was sealed.'
                  : readGate(unwrap(gate.data))
                    ? 'Only worth doing after you have changed something above.'
                    : 'Answers each check from the evidence behind it.'}
              </span>
            </>
          }
        >
          {(() => {
            const g = readGate(unwrap(gate.data));
            return g ? (
              <GateChecks
                gate={g}
                runState={state}
                outcome={
                  note && noteFor === 'keep-all'
                    ? { check: 'Curation decided', message: note }
                    : note && noteFor === 'population'
                      ? { check: 'Population dispositioned', message: note }
                      : null
                }
                // Clearing a criterion has to be possible from where it is
                // named. Pointing at a panel elsewhere on the page dead-ended
                // an operator at exactly the moment they asked what to do.
                actions={[
                  {
                    check: 'Curation decided',
                    label: `Keep all ${undecided(unwrap(curation.data)).length} and clear this`,
                    busy: busy === 'keep-all',
                    run: () => void keepAllFlags(),
                  },
                  // Offered only where the database accepts it. Past
                  // structuring the remedy beside this correctly says nothing
                  // on the page can clear it, and a live button underneath
                  // that sentence promised the opposite.
                  ...(at(...POPULATION_OPEN_STATES)
                    ? [
                        {
                          check: 'Population dispositioned',
                          label: 'Take stock of every file',
                          busy: busy === 'population',
                          run: () =>
                            void act(
                              'population',
                              () => backend.registerPackagingPopulation(runId, newIdempotencyKey()),
                              'Started. It writes one line per file in the background, so a large bucket takes a while; this page keeps watching.',
                            ),
                        },
                      ]
                    : []),
                ]}
              />
            ) : gate.isPending ? (
              <p className="px-4 py-3 text-small text-ink-3">Running the release checks against this run.</p>
            ) : gate.isError ? (
              <p className="px-4 py-3 text-small text-warn">{explainLine(gate.error, 'read the release checks')}</p>
            ) : (
              /* The gate is the claim that a package is fit to hand over. A
                 key/value dump of an unrecognised envelope is a debugging
                 surface, and a partial reading of a release decision is worse
                 than refusing to draw one. */
              <p className="px-4 py-3 text-small text-warn">
                The release checks came back in a shape this screen does not recognise. Nothing is drawn, because a
                partial reading of a gate would be worse than none. Treat this run as unreleased and report it.
              </p>
            );
          })()}
        </StepCard>

      )}

      {at('GATE_PENDING', 'GATE_PASSED', 'DELIVERING', 'DELIVERED', 'DELIVERY_FAILED') && (() => {
        /**
         * The delivery, as the contract now types it.
         *
         * This was a hand-written structural guess because the route answered
         * `AnyDataEnvelope`, and the guess had `startedAt` in it. The field is
         * `requestedAt`, so the elapsed clock on the longest wait in the
         * product read a property that was never there and never rendered.
         */
        const d = unwrap(delivery.data) as PackagingDelivery | null | undefined;
        const started = Boolean(d?.state);
        const done = d?.state === 'DELIVERED';
        // A delivery fails as FAILED. DELIVERY_FAILED is the packaging RUN's
        // state for the same event, and testing the delivery for it was a
        // branch that could never be true - written while the route was
        // untyped and the two vocabularies looked like one.
        //
        // Read through the same predicate the retry uses. Written out twice,
        // the two drifted: this counted a record carrying a code as failed
        // while the retry tested that code against a list, so a delivery could
        // render as a dead end and be ineligible for the recovery that was
        // meant to cover it.
        const failedState = hasFailed(d);
        /**
         * A failure this page is recovering from is not a failure to report.
         *
         * While the retry is in flight the card must read as work in progress,
         * not as a dead end with an instruction beside it - otherwise the
         * operator is being asked to act on something already being handled.
         */
        const recovering = failedState && failedOnStaleSnapshot && canRequestDelivery;
        /**
         * QUEUED is requested, not started.
         *
         * `started` was Boolean(state), so a delivery merely accepted counted
         * as in flight and drew "Copying and verifying every object" over a
         * climbing clock. On aiinsurance26 that ran for minutes against
         * objectsUploaded 0, because the copy had not begun and would not
         * until the run passed its gate. A timer measuring nothing is worse
         * than no timer: it is evidence of progress that does not exist.
         */
        const claimed = Boolean(d?.state) && d?.state !== 'QUEUED';
        const inFlight = claimed && !done && !failedState;
        const queued = d?.state === 'QUEUED';
        return (
          <StepCard
            step={stepNo('deliver')}
            summary="The copy into the buyer's own bucket."
            of={STEP_COUNT}
            state={drawn('deliver')}
            title="Hand it over"
            instruction={
              done
                ? `Delivered into ${d?.bucketName ?? (bucketName.trim() || 'its bucket')}. Every object was uploaded and verified, and the receipt is below.`
                : /* A failed copy fell into the "started" branch and was
                     described as still copying, with live counters under it.
                     The failure is the headline. */
                  failedState
                  ? recovering
                    ? `This did not get through and is being retried automatically (attempt ${attempts + 1} of ${MAX_ATTEMPTS}). Nothing was handed over and nothing was lost; the counters below are the new attempt.`
                    : deliveryFailure(String(d?.failureCode ?? d?.state ?? ''), ship, at('GATE_PASSED'))
                  : started
                    ? `Copying this package into ${d?.bucketName ?? (bucketName.trim() || 'its bucket')}. Every number below is read from the delivery record.`
                    : 'This copies the whole package into a bucket of its own, verifying every object on the way in. The gate is re-proven when you press it, so a pass from earlier authorises nothing.'
            }
            action={
              <>
                {started ? (
                  <Button disabled>{say(String(d?.state))}</Button>
                ) : (
                  <Confirm
                    label="Deliver into its own bucket"
                    confirmLabel={`Copy into ${d?.bucketName ?? (bucketName.trim() || 'the buyer’s bucket')}`}
                    consequence="This copies every placed file into a bucket outside this account."
                    busyLabel="Starting…"
                    loading={busy === 'delivery' || watching === 'delivery'}
                    // Also on the gate having PASSED. Offering it at
                    // GATE_PENDING books a delivery no worker will claim, and
                    // the operator waits on a record that cannot move.
                    disabled={live !== 'deliver' || 
                      !mayDeliver ||
                      busy !== null ||
                      watching !== null ||
                      !ship.willShip ||
                      !at('GATE_PASSED', 'DELIVERING', 'DELIVERED')
                    }
                    onConfirm={() =>
                      void act(
                        'delivery',
                        () => backend.startPackagingDelivery(runId, newIdempotencyKey()),
                        'Delivery requested. Progress appears here as objects are uploaded and verified.',
                        true,
                      )
                    }
                  />
                )}
                <ActionNote when="delivery" noteFor={noteFor} note={note} ok={noteOk} />
                <span className="min-w-0 flex-1 text-small text-ink-2">
                  {/* Three different reasons this does not complete, and they
                      look identical from a progress bar at zero: an empty
                      manifest, a gate that refuses at this boundary, and no
                      worker to claim the record. Each is named on its own. */}
                  {ship.known && !ship.willShip
                    ? `There is nothing to copy: ${ship.detail ?? 'no population is registered'}. A delivery carries only placed files, so this would be refused for an empty manifest. It is registered while a run is structuring, and this one is past that, so a new run is the way to a package that can ship.`
                    : done
                      ? 'Every object was read back and its digest compared against the source. The receipt below is what proves it.'
                      : failedState
                        ? 'The counters above are how far the copy got before it stopped. Nothing was marked delivered, and the objects already written are kept rather than removed.'
                        : started
                          ? // The states are the delivery's own, so the sentence is too: a
                            // copy already verifying was still being told it would start
                            // within five minutes.
                            d?.state === 'VERIFYING'
                            ? 'Every object has been uploaded and each one is now being read back and compared to its source. Verified is the number that counts.'
                            : d?.state === 'CREATING_BUCKET'
                              ? 'The destination bucket is being created. It is a new bucket beside the source, never the source itself.'
                              : d?.state === 'UPLOADING'
                                ? 'Objects are being copied now. Verification is a separate pass that follows, so uploaded runs ahead of verified.'
                                : !at('GATE_PASSED', 'DELIVERING', 'DELIVERED')
                                  ? // A delivery is only claimed once the run has PASSED the gate,
                                    // and reading the gate records no transition - so a run whose
                                    // nine checks all pass still sits at GATE_PENDING until the
                                    // checks are actually run. This said "starts automatically,
                                    // usually within five minutes" over a record that would have
                                    // sat queued indefinitely, with nothing naming the one press
                                    // that releases it.
                                    `This is queued and will stay queued: a delivery is only picked up once the run has passed its checks, and this run is ${say(state).toLowerCase()}. Run the checks in step ${stepNo('gate')} above; the copy starts on its own once they pass.`
                                  : 'A worker claims this and the counters above start moving. They are read from the delivery record, so they are what has actually been copied.'
                          : 'The gate is re-proven the moment you press this, so a pass from earlier authorises nothing and a failing gate records no attempt. The copy then starts automatically, usually within five minutes.'}
                </span>
              </>
            }
          >
            {started && (
              <div className="border-t border-line px-4 py-3">
                {/* Verified is the honest number: an object is uploaded before
                    it is read back and its digest compared, and a delivery is
                    only DELIVERED when every promised object verified. A bar
                    driven off uploads reads complete while the check is still
                    running and can still fail on a digest mismatch. */}
                {/* A counter the record has not reported is unknown, and a
                    zero here means the copy has verified nothing. Rendering the
                    first as the second draws a confident empty bar for a
                    delivery whose progress simply has not been read. */}
                <div className="mono flex flex-wrap gap-x-6 gap-y-1 text-caption text-ink-2">
                  <span>{count(d?.objectsVerified)} of {count(d?.objectsTotal)} objects verified</span>
                  <span>{count(d?.objectsUploaded)} uploaded</span>
                  <span>
                    {typeof d?.bytesUploaded === 'number' ? formatBytes(d.bytesUploaded) : '—'} of{' '}
                    {typeof d?.bytesTotal === 'number' ? formatBytes(d.bytesTotal) : '—'}
                  </span>
                  {d?.region && <span>{d.region}</span>}
                </div>
                {/* A copy of twelve gigabytes is the longest wait in the
                    product and the card counted objects without ever saying
                    how long it had been going. The record carries the start,
                    so the wait is measured rather than guessed at. */}
                {/* Gating this on a failure FIELD meant a record that reports
                    its failure in its state and not in that field kept the
                    spinner running: "Copying and verifying every object, 15m
                    05s" directly above the word "failed". The state is the
                    authority on whether a copy is still moving. */}
                {queued && d?.requestedAt && (
                  <Working
                    what="Queued, waiting for a worker to claim it"
                    usually="nothing has been copied while this says queued"
                    since={Date.parse(d.requestedAt)}
                    overAt={600}
                  />
                )}
                {queued && (
                  <p role="status" className="mt-1 text-small text-ink-2">
                    {at('GATE_PASSED', 'DELIVERING', 'DELIVERED')
                      ? 'Requested and waiting to be picked up. Nothing has been copied yet; the counters start moving when a worker claims it.'
                      : `Requested, and it will not be picked up: a delivery is claimed once the run has passed its checks, and this run is ${say(state).toLowerCase()}. Nothing has been copied, and nothing is being copied now.`}
                  </p>
                )}
                {inFlight && d?.requestedAt && (
                  <Working
                    what="Copying and verifying every object"
                    usually="a package this size usually takes tens of minutes"
                    since={Date.parse(d.requestedAt)}
                    overAt={7200}
                  />
                )}
                {typeof d?.objectsVerified === 'number' && typeof d?.objectsTotal === 'number' && d.objectsTotal > 0 ? (
                  <span className="mt-2 block h-1.5 w-full overflow-hidden rounded-full bg-panel-2">
                    <span
                      className="block h-full rounded-full bg-acc"
                      style={{ width: `${Math.min(100, (d.objectsVerified / d.objectsTotal) * 100)}%` }}
                    />
                  </span>
                ) : (
                  <p className="mt-2 text-caption text-ink-3">
                    No progress has been recorded against this delivery yet.
                  </p>
                )}
                {/* The card's own instruction says this now, and printing it
                    twice on one card reads as two separate faults. */}
                {d?.manifestSha256 && (
                  <p className="mono mt-2 truncate text-caption text-ink-4" title={d.manifestSha256}>
                    receipt manifest {d.manifestSha256}
                  </p>
                )}
              </div>
            )}
          </StepCard>
        );
      })()}

    </>
  );
}

/** Deterministic name candidates the backend proposed for this run. */
function nameCandidates(value: unknown): string[] {
  const d = (value as { deterministic?: unknown[] } | null)?.deterministic;
  return Array.isArray(d) ? d.filter((x): x is string => typeof x === 'string') : [];
}

/** Tool ids in the run's sealed toolset. */
function toolIds(value: unknown): string[] {
  const entries = (value as { entries?: unknown[] } | null)?.entries;
  if (!Array.isArray(entries)) return [];
  return entries
    .map((e) => (e && typeof e === 'object' ? ((e as { toolId?: unknown }).toolId ?? null) : null))
    .filter((t): t is string => typeof t === 'string');
}

/** How many undecided flags the detector proposes to DROP. */
function proposedDrops(value: unknown): number {
  return undecided(value).filter((i) => i.proposal === 'DROP').length;
}

/** Curation flags still awaiting a decision, with the proposal to apply. */
/**
 * The flags nobody has ruled on, through the same reader the rows use.
 *
 * This parsed the payload a second time, independently, and the two readers
 * disagreed about the one field that matters. The backend sends `decision` as
 * an object; this tested it for null and was right, while `readFlags` tested
 * it for a string and turned every decided flag back into an undecided one. So
 * one panel heading said "0 still to decide" directly above rows saying "100
 * to decide", each of them confident.
 *
 * Two parsers of one payload is the defect, not the mismatch between them, so
 * there is one now and the rows and the heading cannot drift apart again.
 */
function undecided(value: unknown): { id: string; proposal: 'KEEP' | 'DROP' }[] {
  return (readFlags(value) ?? [])
    .filter((f) => f.decision === null)
    .map((f) => ({ id: f.id, proposal: (f.proposal === 'KEEP' ? 'KEEP' : 'DROP') as 'KEEP' | 'DROP' }))
    .filter((i) => i.id !== '' && i.id !== 'undefined');
}

/** Drafted artifacts awaiting acceptance, with what an accept must send back. */
function drafts(value: unknown): { key: string; fileName: string; version: number; content: string; state: string }[] {
  const list = (value as { artifacts?: unknown[] } | null)?.artifacts;
  if (!Array.isArray(list)) return [];
  return list
    .filter((a): a is Record<string, unknown> => Boolean(a) && typeof a === 'object')
    .map((a) => ({
      key: String(a.key),
      fileName: String(a.fileName ?? a.key),
      version: Number(a.version ?? 1),
      content: String(a.content ?? ''),
      state: String(a.state ?? ''),
    }))
    .filter((a) => a.key !== 'undefined');
}
